import io from "socket.io-client";
import api from "./api";
import { clearSession, refreshSession } from "./session";

class SocketWorker {
  get connected() {
    return !this.closed && this.socket?.connected === true;
  }

  constructor(companyId, userId) {
    const existing = SocketWorker.instance;
    if (
      existing &&
      (String(existing.companyId) !== String(companyId) ||
        String(existing.userId) !== String(userId))
    ) {
      existing.disconnect();
    }
    if (SocketWorker.instance) return SocketWorker.instance;
    this.companyId = companyId;
    this.userId = userId;
    this.socket = null;
    this.closed = false;
    this.refreshing = false;
    this.refreshAttempts = 0;
    this.reconnectTimer = null;
    this.eventListeners = {};
    SocketWorker.instance = this;
    this.configureSocket();
  }

  configureSocket() {
    if (this.closed) return;
    const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    this.socket = io(`${baseUrl}/${this.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      transports: ["websocket", "polling"],
      tryAllTransports: true,
      timeout: 10000,
      auth: (callback) =>
        callback({ token: api.defaults.headers.Authorization }),
      query: { userId: this.userId },
    });
    this.socket.on("connect_error", async (error) => {
      if (
        this.closed ||
        this.refreshing ||
        error?.data?.code !== "ERR_SESSION_EXPIRED"
      )
        return;
      this.refreshing = true;
      try {
        await refreshSession();
        this.refreshAttempts = 0;
        if (!this.closed) this.socket.connect();
      } catch (refreshError) {
        if ([401, 403].includes(refreshError?.response?.status)) {
          clearSession();
          this.disconnect();
        } else {
          this.scheduleReconnect(Math.min(2000 * 2 ** this.refreshAttempts++, 30000));
        }
      } finally {
        this.refreshing = false;
      }
    });
    this.socket.on("disconnect", reason => {
      if (reason === "io server disconnect") this.scheduleReconnect(1000);
    });
  }

  scheduleReconnect(delay) {
    if (this.closed || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.closed) this.socket?.connect();
    }, delay);
  }

  on(event, callback) {
    if (this.closed) return;
    this.connect();
    this.socket.on(event, callback);
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    if (this.closed) return;
    this.connect();
    this.socket.emit(event, data);
  }

  off(event, callback) {
    if (!this.socket || !this.eventListeners[event]) return;
    if (callback) {
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter(
        (listener) => listener !== callback
      );
    } else {
      this.eventListeners[event].forEach((listener) =>
        this.socket.off(event, listener)
      );
      delete this.eventListeners[event];
    }
  }

  disconnect() {
    this.closed = true;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.eventListeners = {};
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    if (SocketWorker.instance === this) SocketWorker.instance = null;
  }

  connect() {
    if (!this.closed && !this.socket) this.configureSocket();
  }

  forceReconnect() {
    if (this.closed) return;
    this.connect();
    this.socket.disconnect();
    this.socket.connect();
  }
}

export default (companyId, userId) => {
  if (![companyId, userId].every(id => typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id)))) return null;
  const ids = [companyId, userId].map(Number);
  if (!ids.every(id => Number.isSafeInteger(id) && id > 0)) return null;
  return new SocketWorker(...ids);
};
